import {
  JobStatus,
  TERMINAL_JOB_STATES,
} from "@hightouch/lib/job-queue/job-status";
import type { LatestCreativeClassificationRunQuery } from "src/graphql";
import { formatRelativeShort } from "src/utils/time";

/** How often a surface that shows run status checks for a run someone else started. */
export const IDLE_STATUS_POLL_MS = 60_000;

export type CreativeTaggingNamespace = "intelligence" | "competitor";

export type CreativeTaggingStatus =
  | { kind: "never-run" }
  | { kind: "running"; at: string }
  | { kind: "finished"; at: string }
  | { kind: "failed"; at: string };

const TERMINAL_STATUSES = new Set<string>(TERMINAL_JOB_STATES);

export function isRunInProgress(status: string | null | undefined): boolean {
  return Boolean(status) && !TERMINAL_STATUSES.has(status as string);
}

export function creativeTaggingStatusOf(
  data: LatestCreativeClassificationRunQuery | undefined,
): CreativeTaggingStatus {
  const run = data?.amp_creative_classification_runs[0];
  if (!run) return { kind: "never-run" };
  if (isRunInProgress(run.status)) {
    return { kind: "running", at: run.started_at ?? run.created_at };
  }
  const at = run.finished_at ?? run.started_at ?? run.created_at;
  return run.status === JobStatus.Failed
    ? { kind: "failed", at }
    : { kind: "finished", at };
}

/** Why a creative has no tags, from the workspace's latest tagging run. */
export function untaggedMessage(status: CreativeTaggingStatus): string {
  switch (status.kind) {
    case "running":
      return "Tagging is in progress. Tags will appear when it finishes.";
    case "never-run":
      return "No tags yet. Tagging hasn't run for this workspace.";
    case "finished":
      return `No tags yet. Tagging last ran ${formatRelativeShort(status.at)}.`;
    case "failed":
      return `No tags yet. The last tagging run failed ${formatRelativeShort(status.at)}.`;
  }
}

/** The Run tagging button's tooltip: when the current run started, or when the last one ended. */
export function runTaggingTooltip(
  status: CreativeTaggingStatus,
): string | null {
  switch (status.kind) {
    case "running":
      return `Started ${formatRelativeShort(status.at)}`;
    case "never-run":
      return null;
    case "finished":
      return `Last run ${formatRelativeShort(status.at)}`;
    case "failed":
      return `Last run failed ${formatRelativeShort(status.at)}`;
  }
}
